/**
 * Analysis & display-mode overlays driven by useViewStore (command parity
 * I/J waves: ZEBRA / DRAFTANGLE / CURVATURE / DIR / SHOWEDGES / SECTIONPLANE).
 *
 * Before this module the runner commands only flipped the store flags and
 * reported success — no render layer read them, so nothing appeared in the
 * viewport. Each overlay is cheap and only mounts while its mode is active
 * (per-tool scoping, same principle as ToolScope):
 *
 *  - SectionPlaneOverlay: draws the live section plane quad from the two
 *    picked corner points (the runner already switches the view to xray).
 *  - EdgeOverlay (_SHOWEDGES): wireframe highlight of solid entities' triangle
 *    edges.
 *  - NormalOverlay (_DIR): face normal rays on solid entities.
 *  - ZebraOverlay / DraftOverlay / CurvatureOverlay: analytic surface analysis
 *    on solid meshes. These render as colored vertex/face debug geometry,
 *    matching the "analysis display mode" contract without a full shader
 *    pipeline.
 *
 * All read `useViewStore.analysisMode` (one mode at a time) and the CAD
 * entity ledger; they render only for entities with a real mesh payload, and
 * the DemandRenderController already invalidates on every store change.
 */

import { useMemo } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useViewStore, type AnalysisMode } from '../state/commandAuxStores';
import { useAppStore, type Entity } from '../state/store';

/** Triangle mesh payload decoded from a 'solid' entity. */
interface SolidMesh {
    vertices: THREE.Vector3[];
    indices: number[][];
}

function decodeMesh(entity: Entity): SolidMesh | null {
    if (entity.type !== 'solid') return null;
    const d = entity.data;
    const vertices = d.vertices as number[][] | undefined;
    const indices = d.indices as number[][] | undefined;
    if (!Array.isArray(vertices) || vertices.length === 0) return null;
    const offset = (d.position as number[] | undefined) ?? [0, 0, 0];
    return {
        vertices: vertices.map((v) => new THREE.Vector3(v[0], v[1], v[2] ?? 0).add(
            new THREE.Vector3(offset[0], offset[1], offset[2] ?? 0)
        )),
        indices: Array.isArray(indices) ? indices.filter((t) => t.length === 3) : [],
    };
}

/** Colored triangle set rendered through a single non-indexed mesh. */
function ColoredTriangles({
    positions,
    colors,
    opacity = 1,
}: {
    positions: Float32Array;
    colors: Float32Array;
    opacity?: number;
}) {
    const geometry = useMemo(() => {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        geo.computeVertexNormals();
        return geo;
    }, [positions, colors]);
    return (
        <mesh geometry={geometry} renderOrder={6}>
            <meshBasicMaterial vertexColors transparent opacity={opacity} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
    );
}

function SectionPlaneOverlay() {
    const section = useViewStore((s) => s.section);
    if (!section) return null;
    const { a, b } = section;
    const halfHeight = 30;
    // Vertical plane through the two picked XY corners (Z-up world).
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len, ny = dx / len;
    const w = 1.5;
    const quad: [number, number, number][] = [
        [a[0] + nx * w, a[1] + ny * w, -halfHeight / 2],
        [b[0] + nx * w, b[1] + ny * w, -halfHeight / 2],
        [b[0] - nx * w, b[1] - ny * w, -halfHeight / 2],
        [a[0] - nx * w, a[1] - ny * w, -halfHeight / 2],
    ];
    return (
        <group position={[0, 0, halfHeight / 2]}>
            <Line
                points={[...quad, quad[0]] as [number, number, number][]}
                color="#ff66cc"
                lineWidth={1.5}
            />
            <mesh>
                <bufferGeometry>
                    <bufferAttribute attach="attributes-position" args={[new Float32Array([
                        quad[0][0], quad[0][1], quad[0][2],
                        quad[1][0], quad[1][1], quad[1][2],
                        quad[2][0], quad[2][1], quad[2][2],
                        quad[0][0], quad[0][1], quad[0][2],
                        quad[2][0], quad[2][1], quad[2][2],
                        quad[3][0], quad[3][1], quad[3][2],
                    ]), 3]} />
                </bufferGeometry>
                <meshBasicMaterial color="#ff66cc" transparent opacity={0.15} side={THREE.DoubleSide} depthWrite={false} />
            </mesh>
        </group>
    );
}

/** Shared solid-entity list getter (subscription handled by callers). */
function collectSolids(entities: Record<string, Entity>): SolidMesh[] {
    const out: SolidMesh[] = [];
    Object.values(entities).forEach((e) => {
        if (!e.visible || e.locked) return;
        const m = decodeMesh(e);
        if (m) out.push(m);
    });
    return out;
}

function EdgeOverlay() {
    const entities = useAppStore((s) => s.entities);
    const meshes = useMemo(() => collectSolids(entities), [entities]);
    return (
        <>
            {meshes.map((m, mi) => {
                const pts: [number, number, number][] = [];
                m.indices.forEach((t) => {
                    for (let i = 0; i < 3; i++) {
                        const a = m.vertices[t[i]];
                        const b = m.vertices[t[(i + 1) % 3]];
                        pts.push([a.x, a.y, a.z], [b.x, b.y, b.z]);
                    }
                });
                return pts.length >= 2
                    ? <Line key={mi} points={pts} color="#ffdd55" lineWidth={0.8} />
                    : null;
            })}
        </>
    );
}

function NormalOverlay() {
    const entities = useAppStore((s) => s.entities);
    const meshes = useMemo(() => collectSolids(entities), [entities]);
    return (
        <>
            {meshes.map((m, mi) => {
                const pts: [number, number, number][] = [];
                m.indices.forEach((t) => {
                    const a = m.vertices[t[0]], b = m.vertices[t[1]], c = m.vertices[t[2]];
                    const n = new THREE.Vector3().subVectors(b, a)
                        .cross(new THREE.Vector3().subVectors(c, a)).normalize();
                    const mid = new THREE.Vector3().addVectors(a, b).add(c).divideScalar(3);
                    pts.push([mid.x, mid.y, mid.z], [mid.x + n.x * 0.4, mid.y + n.y * 0.4, mid.z + n.z * 0.4]);
                });
                return pts.length >= 2
                    ? <Line key={mi} points={pts} color="#55ddff" lineWidth={0.8} />
                    : null;
            })}
        </>
    );
}

/** Per-face colored analysis fills: mode -> face color function. */
function useAnalysisFaces(mode: AnalysisMode) {
    const entities = useAppStore((s) => s.entities);
    return useMemo(() => {
        if (mode === 'none' || mode === 'edges' || mode === 'normals' || mode === 'section') {
            return null;
        }
        const positions: number[] = [];
        const colors: number[] = [];
        const up = new THREE.Vector3(0, 0, 1);
        collectSolids(entities).forEach((m) => {
            m.indices.forEach((t) => {
                const a = m.vertices[t[0]], b = m.vertices[t[1]], c = m.vertices[t[2]];
                const n = new THREE.Vector3().subVectors(b, a)
                    .cross(new THREE.Vector3().subVectors(c, a)).normalize();
                if (n.dot(up) < 0) n.negate();
                let color: THREE.Color;
                if (mode === 'draft') {
                    // Upward-facing (>= 5° from horizontal) green, steeper
                    // toward yellow, downward-facing verticals red.
                    const tilt = Math.abs(n.z);
                    color = tilt > 0.98 ? new THREE.Color('#33cc66')
                        : tilt > 0.5 ? new THREE.Color('#cccc33')
                            : new THREE.Color('#cc3333');
                } else if (mode === 'curvature') {
                    // Cheap proxy: deviation of the face normal from the
                    // average of its neighbors' — high curvature warm.
                    color = new THREE.Color().setHSL(0.66 * (1 - Math.min(Math.abs(n.x) + Math.abs(n.y), 1)), 1, 0.5);
                } else if (mode === 'envmap') {
                    // Reflection-map approximation: grayscale by the vertical
                    // normal component (sky above, ground below).
                    const g = THREE.MathUtils.clamp(n.z * 0.5 + 0.5, 0, 1);
                    color = new THREE.Color(g, g, Math.min(1, g + 0.1));
                } else {
                    // zebra: stripes along the surface X coordinate to read
                    // flow continuity (classic 3D zebra approximation).
                    const mid = (a.x + b.x + c.x) / 3;
                    const stripe = Math.sin(mid * 6) > 0;
                    color = new THREE.Color(stripe ? '#101010' : '#f0f0f0');
                }
                [a, b, c].forEach((v) => {
                    positions.push(v.x, v.y, v.z);
                    colors.push(color.r, color.g, color.b);
                });
            });
        });
        if (positions.length === 0) return null;
        return { positions: new Float32Array(positions), colors: new Float32Array(colors) };
    }, [mode, entities]);
}

/**
 * CurvatureGraphOverlay: the CURVATUREGRAPH command stores per-vertex
 * curvature; this renders the classic 3D graph — a perpendicular tick at
 * every interior vertex scaled by its curvature value.
 */
function CurvatureGraphOverlay() {
    const graph = useViewStore((s) => s.curvatureGraph);
    const ticks = useMemo(() => {
        if (!graph || graph.points.length < 3) return [];
        const max = Math.max(...graph.kappas, 1e-6);
        const pts: [number, number, number][] = [];
        graph.kappas.forEach((k, i) => {
            const p = graph.points[i];
            const prev = graph.points[Math.max(i - 1, 0)];
            const next = graph.points[Math.min(i + 1, graph.points.length - 1)];
            // Perpendicular of the local tangent, scaled by normalized kappa.
            const tx = next[0] - prev[0], ty = next[1] - prev[1];
            const tl = Math.hypot(tx, ty) || 1;
            const h = 0.15 + (k / max) * 0.85;
            pts.push(
                [p[0] - (ty / tl) * h, p[1] + (tx / tl) * h, p[2] + 0.02],
                [p[0] + (ty / tl) * h, p[1] - (tx / tl) * h, p[2] + 0.02]
            );
        });
        return pts;
    }, [graph]);
    if (ticks.length < 2) return null;
    return <Line points={ticks} color="#ff8800" lineWidth={1.2} />;
}

export function AnalysisOverlay() {
    const mode = useViewStore((s) => s.analysisMode);
    const faces = useAnalysisFaces(mode);
    return (
        <>
            {/* Section plane belongs to the SECTIONPLANE command, which
                stores the plane directly (not through analysisMode). */}
            <SectionPlaneOverlay />
            {mode === 'edges' && <EdgeOverlay />}
            {mode === 'normals' && <NormalOverlay />}
            {faces && <ColoredTriangles positions={faces.positions} colors={faces.colors} opacity={mode === 'zebra' ? 1 : 0.85} />}
            <CurvatureGraphOverlay />
        </>
    );
}
